import { useMutation } from '@apollo/client';
import upperFirst from 'lodash/upperFirst';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  SubmittablePrompt,
  SubmittablePromptSchema,
} from '@eluve/api-contract';
import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Icon,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@eluve/components';
import { formatHumanName } from '@eluve/utils';

import { TemplateVariant, TemplateVariantCard } from './TemplateVariantCard';
import {
  promptTemplateVariantsFragment,
  setDefaultPromptTemplateVariantMutation,
} from './prompt.operations';

type PromptTemplateVariant = TemplateVariant & {
  template?: SubmittablePrompt;
};

type PromptTemplateDetailsPromptTextProps = {
  promptTemplateId: string;
  variantNumber?: string;
};

export const PromptTemplateDetailsPromptText: React.FC<
  PromptTemplateDetailsPromptTextProps
> = ({ promptTemplateId, variantNumber: selectedVariantNumber }) => {
  const navigate = useNavigate();
  const toast = useToast();

  const [setDefaultPromptTemplateVariant] = useMutation(
    setDefaultPromptTemplateVariantMutation,
  );

  const data = useCompleteFragment({
    fragment: promptTemplateVariantsFragment,
    key: { id: promptTemplateId },
  });

  const variants: PromptTemplateVariant[] = useMemo(
    () =>
      (data.prompt_template_variants ?? []).map((variant) => ({
        variantNumber: variant.variantNumber,
        template: variant.template as [],
        isCurrentDefault: variant.isCurrentDefault,
        variantNotes: variant.variantNotes,
        createdAt: variant.createdAt,
        createdBy: !variant.creator
          ? undefined
          : formatHumanName(
              variant.creator?.firstName,
              variant.creator?.lastName,
            ),
      })),
    [data.prompt_template_variants],
  );

  const currentDefaultVariant = variants.find(
    (variant) => variant.isCurrentDefault,
  );
  const selectedVariant = selectedVariantNumber
    ? variants.find(
        (variant) => variant.variantNumber === Number(selectedVariantNumber),
      )
    : currentDefaultVariant;
  const selectedTemplate = selectedVariant?.template;

  if (!selectedTemplate) {
    return (
      <Alert>
        <AlertTitle>Prompt Template Not Available</AlertTitle>
        <AlertDescription>
          This is a legacy template that should be disabled.
        </AlertDescription>
      </Alert>
    );
  }

  const parts = SubmittablePromptSchema.parse(selectedTemplate);

  const handleCardClick = (variantNumber: number) => {
    navigate(
      `/admin/prompts/${promptTemplateId}/prompt/variant/${variantNumber}`,
    );
  };

  const handleDuplicateClick = (variantNumber: number) => {
    navigate(
      `/admin/prompts/clone/${promptTemplateId}/variant/${variantNumber}`,
    );
  };

  const handleSetAsDefaultClick = async (variantNumber: number) => {
    if (!currentDefaultVariant) {
      toast.error('We could not set this variant as the default.');
      return;
    }

    if (selectedVariantNumber == null) {
      handleCardClick(currentDefaultVariant.variantNumber);
    }

    await setDefaultPromptTemplateVariant({
      variables: { promptTemplateId, variantNumber },
      onError: () => {
        toast.error('We could not set this variant as the default.');
      },
      optimisticResponse: () => ({
        updatePromptTemplateVariants: {
          returning: [
            {
              __typename: 'PromptTemplateVariants' as const,
              promptTemplateId,
              variantNumber: currentDefaultVariant.variantNumber,
              isCurrentDefault: false,
            },
          ],
        },
        updatePromptTemplateVariantsByPk: {
          __typename: 'PromptTemplateVariants' as const,
          promptTemplateId,
          variantNumber,
          isCurrentDefault: true,
        },
      }),
    });
  };

  return (
    <Stack align="stretch" className="sm:flex-row" gap={5}>
      <Stack gap={6}>
        {parts.map((part) => (
          <Stack align="stretch" key={part.role} gap={2}>
            <Stack gap={2} direction="horizontal">
              <Text inline size="md">{`${upperFirst(part.role)} Message`}</Text>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={async () => {
                      await navigator.clipboard.writeText(part.content);
                      toast.success(
                        `${upperFirst(part.role)} Message copied to clipboard`,
                      );
                    }}
                    aria-label={`Copy ${part.role} message`}
                  >
                    <Icon name="Copy" size="auto" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Copy to clipboard</TooltipContent>
              </Tooltip>
            </Stack>
            <div className="max-h-96 overflow-y-auto rounded-md border border-border-primary bg-bg-secondary p-5">
              <pre className="text-sm font-medium text-wrap text-text-primary">
                {part.content}
              </pre>
            </div>
          </Stack>
        ))}
      </Stack>
      <div className="h-px w-auto shrink-0 bg-border-primary sm:h-auto sm:w-px" />
      <Stack className="flex-1 pt-2 sm:min-w-60 md:min-w-72" gap={2}>
        <Text size="md">Variants</Text>
        <Stack align="stretch" className="mt-2" gap={6}>
          {variants.map((variant) => (
            <TemplateVariantCard
              isSelected={
                variant.variantNumber === selectedVariant.variantNumber
              }
              key={variant.variantNumber}
              onClick={() => handleCardClick(variant.variantNumber)}
              onDuplicateClick={() =>
                handleDuplicateClick(variant.variantNumber)
              }
              onSetAsDefaultClick={() =>
                handleSetAsDefaultClick(variant.variantNumber)
              }
              variant={variant}
            />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export const PromptTemplateDetailsPromptTextPage: React.FC = () => {
  const { promptTemplateId, variantNumber } = useParams() as {
    promptTemplateId: string;
    variantNumber?: string;
  };
  return (
    <PromptTemplateDetailsPromptText
      promptTemplateId={promptTemplateId}
      variantNumber={variantNumber}
    />
  );
};
